<template>
  <div>
    <select
        :ref="initRef"
        v-model="model"
        v-show="instance === null"
        :disabled="instance ? false : actualDisabled"
    />
  </div>
</template>
<script setup>
/*

Just select tag w/ typeahead support,
model is a primitive value as defined in "keys" prop

*/

import { computed, inject, nextTick, onUnmounted, onMounted, ref, shallowRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import Choices from 'choices.js'
import { applyViewportMenu, bindChoicesMenuEvents, resetViewportMenu } from '@/lib/overlayClip'

const { t } = useI18n()

const model = defineModel()
const props = defineProps({
  options: {
    type: Array,
    default: () => [],
  },
  keys: {
    type: String,
    default: 'id',
  },
  values: {
    type: [String, Function],
    default: 'name',
  },
  shouldSelect: {
    type: Function,
    default: x => Number(x),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  autoSort: {
    type: Boolean,
    default: true,
  },
  addItemText: {
    type: String,
  },
  addItemCallback: {
    type: Function,
  },
  ignoreFormEditable: { type: Boolean, default: false },
})

const actualDisabled = computed(() => {
  if (props.ignoreFormEditable) return props.disabled ?? false
  if (formEditable !== null) return !formEditable.value || props.disabled
  return props.disabled
})

const addItemKey = '__addItem'

const instance = ref(null)
const elem = ref(null)
// Открытие само фокусирует поиск и переносит меню на body — это даёт scroll/blur,
// и список тут же закрывается («мигает и не открывается»).
let suppressScrollCloseUntil = 0

function armOpenGuards() {
  suppressScrollCloseUntil = performance.now() + 400
}

function focusWithoutPageScroll(el) {
  if (!(el instanceof HTMLElement) || el.dataset.ltmsNoScrollFocus === '1') return
  const orig = el.focus.bind(el)
  el.focus = options => {
    const extra = options && typeof options === 'object' ? options : {}
    orig({ preventScroll: true, ...extra })
  }
  el.dataset.ltmsNoScrollFocus = '1'
}

function getDropdownEl() {
  const inst = instance.value
  // Choices v10+: dropdown.element exists
  const el = inst?.dropdown?.element
  return el instanceof HTMLElement ? el : null
}

function updateDropdownPosition() {
  const inst = instance.value
  const dropdownEl = getDropdownEl()
  if (!inst || !dropdownEl) return

  const outer = inst.containerOuter?.element
  if (!(outer instanceof HTMLElement)) return

  if (!inst.dropdown?.isActive) return
  const input = inst.input?.element
  const hadFocus = input instanceof HTMLElement && document.activeElement === input
  armOpenGuards()
  // Перенос сфокусированного input на body шлёт blur, и Choices закрывает список.
  const origHide = inst.hideDropdown
  inst.hideDropdown = () => inst
  try {
    applyViewportMenu(outer.getBoundingClientRect(), dropdownEl)
  } finally {
    inst.hideDropdown = origHide
  }
  if (hadFocus && input instanceof HTMLElement && document.activeElement !== input) {
    input.focus({ preventScroll: true })
  }
}

function resetDropdownPosition() {
  resetViewportMenu(getDropdownEl())
}

/** Скролл формы — закрыть список (иначе fixed-меню «ездит» по карточке). */
function closeDropdownOnScroll(event) {
  if (performance.now() < suppressScrollCloseUntil) return
  const inst = instance.value
  if (!inst?.dropdown?.isActive) return
  const dropdownEl = getDropdownEl()
  const target = event?.target
  // Скролл внутри самого списка не закрываем.
  if (
    target instanceof Node
    && dropdownEl
    && (dropdownEl === target || dropdownEl.contains(target))
  ) {
    return
  }
  try {
    inst.hideDropdown?.()
  } catch {
    /* ignore */
  }
}

/** Ошибочный поиск остаётся в input — при следующем открытии снова «No results». */
function clearSearchOnHide() {
  resetDropdownPosition()
  try {
    instance.value?.clearInput?.()
  } catch {
    /* ignore */
  }
}

function ensureChoicesVisibleOnShow() {
  armOpenGuards()
  updateDropdownPosition()
  nextTick(() => updateDropdownPosition())
  const inst = instance.value
  if (!inst) return
  const q = String(inst.input?.element?.value || '').trim()
  if (!q) return
  const hasSelectable = Boolean(
    inst.dropdown?.element?.querySelector?.('.choices__item--selectable:not(.choices__placeholder)')
  )
  if (!hasSelectable) {
    try {
      inst.clearInput?.()
    } catch {
      /* ignore */
    }
  }
}

const initRef = el => {
  if (!instance.value) {
    instance.value = new Choices(el, {
      allowHTML: true,
      shouldSort: props.autoSort,
      resetScrollPosition: false,
      searchEnabled: true,
      searchResultLimit: -1,
      searchFields: [props.values, 'label'],
      itemSelectText: '',
      addChoices: false,
      addItems: false,
      editItems: false,
      fuseOptions: {
        shouldSort: true,
        findAllMatches: true,
        ignoreLocation: true,
        threshold: 0.4,
      },
      sorter: (a, b) => {
        if (a.value == addItemKey || b.value == addItemKey) {
          return -1
        }

        return a.label > b.label ? 1 : -1
      },
    })

    bindChoicesMenuEvents(instance.value)
    focusWithoutPageScroll(instance.value.input?.element)
    focusWithoutPageScroll(instance.value.containerOuter?.element)
    instance.value.containerOuter?.element?.addEventListener('pointerdown', armOpenGuards)
    instance.value.containerOuter?.element?.addEventListener('keydown', armOpenGuards)
    el.addEventListener('choice', onChoice)
    el.addEventListener('showDropdown', ensureChoicesVisibleOnShow)
    el.addEventListener('hideDropdown', clearSearchOnHide)
    elem.value = el
    setall()
    // Устанавливаем начальное состояние disabled после инициализации
    // setall() уже устанавливает состояние в конце, но на всякий случай делаем еще раз
    if (actualDisabled.value) {
      instance.value.disable()
    } else {
      instance.value.enable()
    }
  }
}

const formEditable = inject('form:editable', () => shallowRef(true), true)
onUnmounted(() => {
  try {
    const outer = instance.value?.containerOuter?.element
    outer?.removeEventListener('pointerdown', armOpenGuards)
    outer?.removeEventListener('keydown', armOpenGuards)
    elem.value?.removeEventListener('showDropdown', ensureChoicesVisibleOnShow)
    elem.value?.removeEventListener('hideDropdown', clearSearchOnHide)
    elem.value?.removeEventListener('choice', onChoice)
  } finally {
    resetDropdownPosition()
    instance.value?.destroy?.()
  }
})

onMounted(() => {
  window.addEventListener('resize', updateDropdownPosition)
  window.addEventListener('scroll', closeDropdownOnScroll, true)
})

onUnmounted(() => {
  window.removeEventListener('resize', updateDropdownPosition)
  window.removeEventListener('scroll', closeDropdownOnScroll, true)
})

watch(
    () => props.options,
    () => setall(),
    { immediate: true }
)
watch(model, setall, { immediate: true })
watch(
    actualDisabled,
    (disabled) => {
      if (!instance.value) {
        return
      }

      // Устанавливаем disabled на нативном элементе и через Choices.js API
      const nativeElement = instance.value.passedElement.element
      if (nativeElement) {
        nativeElement.disabled = disabled
      }

      if (disabled) {
        instance.value.disable()
      } else {
        instance.value.enable()
      }
    },
    { immediate: true }
)

function setall() {
  if (!instance.value) {
    return
  }
  let options = Array.isArray(props.options) ? [...props.options] : []
  let values = props.values

  if (props.values instanceof Function) {
    values = '__label'
    options = options.map(o => {
      o[values] = props.values(o)
      return o
    })
  }
  if (props.addItemCallback instanceof Function) {
    let label = props.addItemText || t('addItem')
    options.unshift({
      [props.keys]: addItemKey,
      [values]: label,
    })
  }
  const reopen = Boolean(instance.value.dropdown?.isActive)
  if (reopen) armOpenGuards()
  instance.value.clearStore()
  instance.value.setChoices(options, props.keys, values, true)
  if (model.value) {
    instance.value.setChoiceByValue(props.shouldSelect(model.value))
  } else {
    instance.value.removeActiveItems()
  }
  // Восстанавливаем состояние disabled после обновления опций
  if (actualDisabled.value) {
    instance.value.disable()
  } else {
    instance.value.enable()
  }
  if (reopen && !instance.value.dropdown?.isActive) {
    instance.value.showDropdown(true)
  }

  // Если дропдаун открыт — пересчитать позицию (например, после изменения опций).
  nextTick(() => updateDropdownPosition())
}

function onChoice({ detail }) {
  if (detail.value == addItemKey) {
    setall()
    props.addItemCallback()
  }
}
</script>

<i18n>
{
  "ru": {
    "addItem": "Добавить"
  },
  "en": {
    "addItem": "Add"
  }
}
</i18n>
